import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, FileText, Download, Loader, Bookmark, ExternalLink, ChevronDown, ChevronUp, Sparkles, Languages } from 'lucide-react';

export default function ArticleDetail() {
  const { articleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [article, setArticle] = useState(location.state?.article);
  const [aiSummary, setAiSummary] = useState('');
  const [summarizing, setSummarizing] = useState(false);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [language, setLanguage] = useState('en');
  const [expandedSections, setExpandedSections] = useState({});

  // Auto-generate AI summary when article loads
  useEffect(() => {
    if (article && !aiSummary) {
      generateAiSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const generateAiSummary = async () => {
    if (!article) return;
    setSummarizing(true);
    try {
      const response = await axios.post(`${API}/intelligence/summarize`, {
        article_id: article.id,
        article_title: article.title,
        article_content: article.summary,
      });
      setAiSummary(response.data.summary);
    } catch (error) {
      console.error('Error generating summary:', error);
      setAiSummary('Unable to generate AI summary at this time.');
    } finally {
      setSummarizing(false);
    }
  };

  const translateToArabic = async () => {
    setTranslating(true);
    try {
      const contentToTranslate = report 
        ? JSON.stringify(report) 
        : JSON.stringify({ title: article.title, summary: aiSummary });
      
      const response = await axios.post(`${API}/intelligence/translate`, {
        content: contentToTranslate,
        target_language: language === 'en' ? 'ar' : 'en',
      });
      
      if (language === 'en') {
        setLanguage('ar');
        if (report) {
          setReport(response.data.translated_content);
        } else {
          setAiSummary(response.data.translated_content.summary);
        }
      } else {
        setLanguage('en');
        // Regenerate original content
        generateAiSummary();
        if (report) {
          generateReport();
        }
      }
    } catch (error) {
      console.error('Error translating:', error);
      alert('Translation failed. Please try again.');
    } finally {
      setTranslating(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/intelligence/generate`, {
        article_id: article.id,
        article_title: article.title,
        article_content: article.summary,
      });
      setReport(response.data);
      // Expand all sections by default
      setExpandedSections({
        executive: true,
        situation: true,
        geopolitical: true,
        military: true,
        historical: true,
        future: true,
      });
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate intelligence report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const exportToPptx = async () => {
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/intelligence/export-pptx/${report.id}`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `intel_report_${report.id}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PPTX:', error);
      alert('Failed to export PowerPoint. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const saveBookmark = async () => {
    try {
      await axios.post(`${API}/bookmarks`, {
        article_id: article.id,
        article_title: article.title,
        article_url: article.url,
      });
      alert('Article bookmarked successfully!');
    } catch (error) {
      console.error('Error bookmarking:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const ReportSection = ({ title, content, sectionKey }) => (
    <div className="glass-panel border border-white/10 mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
      >
        <h3 className="text-sm font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h3>
        {expandedSections[sectionKey] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expandedSections[sectionKey] && (
        <div className="px-4 pb-4 text-sm text-zinc-300 leading-relaxed border-t border-white/10 pt-4">
          {content}
        </div>
      )}
    </div>
  );

  if (!article) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 font-mono text-lg mb-4">Article not found</p>
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary px-6 py-2"
          >
            <ArrowLeft className="w-4 h-4 inline mr-2" />
            BACK TO NEWS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="article-detail">
      {/* Header */}
      <div className="sticky top-0 z-10 glass-panel border-b border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-bold uppercase tracking-wider">Back</span>
          </button>
          <div className="flex gap-2">
            <button
              data-testid="bookmark-button"
              onClick={saveBookmark}
              className="flex items-center gap-2 px-4 py-2 btn-secondary"
            >
              <Bookmark className="w-4 h-4" />
              <span>BOOKMARK</span>
            </button>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 btn-secondary"
            >
              <ExternalLink className="w-4 h-4" />
              <span>SOURCE</span>
            </a>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="p-6 max-w-5xl mx-auto">
        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono">{article.source}</span>
            <span className="text-zinc-600">•</span>
            <span className="text-[10px] text-zinc-400 font-mono">{new Date(article.published).toLocaleString()}</span>
            {article.region && (
              <>
                <span className="text-zinc-600">•</span>
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono">{article.region}</span>
              </>
            )}
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>{article.title}</h1>
          <p className="text-zinc-300 leading-relaxed">{article.summary}</p>
        </div>

        {/* Translation Button */}
        <div className="mb-6 flex justify-end">
          <button
            data-testid="translate-button"
            onClick={translateToArabic}
            disabled={translating}
            className="flex items-center gap-2 px-4 py-2 btn-secondary disabled:opacity-50"
          >
            {translating ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Languages className="w-4 h-4" />
            )}
            <span>{language === 'en' ? 'TRANSLATE TO ARABIC' : 'TRANSLATE TO ENGLISH'}</span>
          </button>
        </div>

        {/* AI Summary Section */}
        <div className="glass-panel tactical-border p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-[#00FF9D]" />
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>AI-POWERED SUMMARY</h2>
          </div>
          {summarizing ? (
            <div className="flex items-center gap-3 text-zinc-400">
              <Loader className="w-5 h-5 animate-spin" />
              <span className="text-sm font-mono">Generating intelligent summary...</span>
            </div>
          ) : (
            <div className="text-zinc-300 leading-relaxed text-base">
              {aiSummary || 'AI summary will appear here...'}
            </div>
          )}
        </div>

        {/* Generate Report Button */}
        {!report && (
          <div className="glass-panel tactical-border p-8 text-center mb-8">
            <FileText className="w-12 h-12 text-[#00FF9D] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>AI INTELLIGENCE ANALYSIS</h2>
            <p className="text-sm text-zinc-400 mb-6 max-w-xl mx-auto">
              Generate a comprehensive intelligence report with geopolitical analysis, military significance assessment, and future projections.
            </p>
            <button
              data-testid="generate-report-button"
              onClick={generateReport}
              disabled={generating}
              className="btn-primary px-8 py-3 disabled:opacity-50"
            >
              {generating ? (
                <span className="flex items-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  GENERATING ANALYSIS...
                </span>
              ) : (
                'GENERATE INTELLIGENCE REPORT'
              )}
            </button>
          </div>
        )}

        {/* Intelligence Report */}
        {report && (
          <div className="mb-8" data-testid="intelligence-report">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>INTELLIGENCE REPORT</h2>
                <p className="text-xs text-zinc-400 font-mono mt-1">
                  Generated: {new Date(report.generated_at).toLocaleString()} | Classification: UNCLASSIFIED
                </p>
              </div>
              <button
                data-testid="export-pptx-button"
                onClick={exportToPptx}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 btn-primary disabled:opacity-50"
              >
                {exporting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>EXPORT POWERPOINT</span>
              </button>
            </div>

            <ReportSection title="Executive Summary" content={report.executive_summary} sectionKey="executive" />
            <ReportSection title="Situation Analysis" content={report.situation_analysis} sectionKey="situation" />
            <ReportSection title="Geopolitical Impact" content={report.geopolitical_impact} sectionKey="geopolitical" />
            <ReportSection title="Military Significance" content={report.military_significance} sectionKey="military" />
            <ReportSection title="Historical Context" content={report.historical_context} sectionKey="historical" />
            <ReportSection title="Future Projections" content={report.future_projections} sectionKey="future" />

            {/* OSINT Sources */}
            <div className="glass-panel border border-white/10 p-4">
              <h3 className="text-sm font-bold uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>OSINT Sources</h3>
              <ul className="space-y-2">
                {report.osint_sources.map((source, idx) => (
                  <li key={idx} className="text-xs text-zinc-400 font-mono flex items-center gap-2">
                    <span className="text-[#00FF9D]">•</span>
                    {source}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}